/**
 * Integration Categories Configuration
 *
 * This file defines the complete category structure and integration mappings.
 * Each category groups related integrations for UI organization and filtering.
 *
 */

import type { CategoryMapping } from '@/features/integrations/types/category.types';
import { IntegrationCategory } from '@/features/integrations/types/category.types';
import { AllIntegrations } from '@/hooks/useIntegration';
import { Integration, IntegrationMeta } from '@/types';

/**
 * Complete category mapping for all integrations
 * Maps each category to its array of integration IDs
 */
export const INTEGRATION_CATEGORIES: CategoryMapping = {
  [IntegrationCategory.ApplicationSecurityPosture]: [
    Integration.apiiro,
    Integration['snyk-apprisk'],
  ],

  [IntegrationCategory.ApplicationSecurityTesting]: [
    Integration.burpsuite,
    Integration.invicti,
    Integration.snyk_dast,
  ],

  [IntegrationCategory.BreachAttackSimulation]: [
    Integration.attackiq,
    Integration.cymulate,
    Integration.safebreach,
  ],

  [IntegrationCategory.BugBounty]: [Integration.hackerone],

  [IntegrationCategory.CloudSecurityPosture]: [
    Integration.jupiterone,
    Integration.orca,
    Integration.wiz,
    Integration.cortex,
    Integration['cortex-xsiam'],
  ],

  [IntegrationCategory.CloudServiceProviders]: [
    Integration.amazon,
    Integration.digitalocean,
    Integration.gcp,
    Integration.azure,
  ],

  [IntegrationCategory.ContentDeliverySolutions]: [Integration.fastly],

  [IntegrationCategory.DiscoveryEnrichment]: [
    Integration.pius_shodan,
    Integration.pius_dnsdb,
    Integration.pius_crunchbase,
    Integration.pius_opencorporates,
    Integration.pius_proxycurl,
    Integration.pius_diffbot,
    Integration.pius_github,
    Integration.pius_securitytrails,
    Integration.pius_virustotal,
    Integration.pius_binaryedge,
    Integration.pius_apollo,
    Integration.pius_censys,
    Integration.pius_viewdns,
  ],

  [IntegrationCategory.CyberAssetAttackSurface]: [
    Integration.axonius,
    Integration.censys_asm,
    Integration.cycognito,
    Integration.flexera,
    Integration.runzero,
    Integration.sevco,
    Integration.xpanse,
    Integration.sharphound,
    Integration.avalor,
  ],

  [IntegrationCategory.Firewall]: [
    Integration.cloudflarewaf,
    Integration['akamai-waf'],
    Integration.aws_waf,
    Integration.palo,
    Integration.imperva,
    Integration.threatx,
    Integration.vercel,
  ],

  [IntegrationCategory.ITServiceManagement]: [
    Integration['azuredevops-workitems'],
    Integration['azuredevops-workitems-pat'],
    Integration.confluence,
    Integration.freshdesk,
    Integration.freshservice,
    Integration.jira,
    Integration.webhook,
    Integration.servicenow,
    Integration.hook,
  ],

  [IntegrationCategory.IdentityAccessManagement]: [
    Integration.entraid,
    Integration.okta,
    Integration.pingone,
    Integration.sailpoint,
  ],

  [IntegrationCategory.ManagedDetectionResponse]: [
    Integration.crowdstrike,
    Integration['crowdstrike-flightcontrol'],
    Integration['microsoft-defender'],
    Integration.sentinelone,
    Integration.intune,
    Integration.extrahop,
  ],

  [IntegrationCategory.ManagedDNSProviders]: [
    Integration.akamaidns,
    Integration.godaddy,
    Integration.ns1,
    Integration.cloudflare, // Cloudflare DNS (base)
  ],

  [IntegrationCategory.PassiveDNSProviders]: [Integration.shodan],

  [IntegrationCategory.SecurityRatings]: [Integration.securityscorecard],

  [IntegrationCategory.SourceCodeManagers]: [
    Integration.azuredevops,
    Integration['azuredevops-pat'],
    Integration.bitbucket,
    Integration.github,
    Integration.gitlab,
  ],

  [IntegrationCategory.ThreatIntelligence]: [
    Integration.recordedfuture,
    Integration.socradar,
  ],

  [IntegrationCategory.VulnerabilityManagement]: [
    Integration.nessus,
    Integration.qualys,
    Integration.insightvm,
    Integration.tenablevm,
  ],

  [IntegrationCategory.WorkplaceMessaging]: [
    Integration.email,
    Integration.slack,
    Integration.teams,
    Integration.zulip,
    Integration.gchat,
  ],

  [IntegrationCategory.SaaSSecurity]: [],

  [IntegrationCategory.SIEM]: [Integration.splunk, Integration.panther],

  [IntegrationCategory.WebApplicationHosting]: [],

  [IntegrationCategory.SecureCodeScanning]: [
    Integration.checkmarx,
    Integration.snyk_code,
  ],
};

/**
 * Get all integrations for a specific category
 *
 * @param category - Integration category
 * @returns Array of IntegrationMeta objects in the category
 */
export function getCategoryIntegrations(
  category: IntegrationCategory
): IntegrationMeta[] {
  const integrationIds = INTEGRATION_CATEGORIES[category] || [];
  return integrationIds
    .map(id => AllIntegrations[id])
    .filter(integration => !integration?.hidden);
}

/**
 * Get category for a specific integration
 * Note: Some integrations may appear in multiple categories (e.g., CrowdStrike)
 *
 * @param integration - Integration ID
 * @returns Primary category or undefined if not found
 */
export function getIntegrationCategory(
  integration: Integration
): IntegrationCategory | undefined {
  for (const [category, integrations] of Object.entries(
    INTEGRATION_CATEGORIES
  )) {
    if (integrations.includes(integration)) {
      return category as IntegrationCategory;
    }
  }
  return undefined;
}

/**
 * Get all categories an integration belongs to
 * (Some integrations may be in multiple categories)
 *
 * @param integration - Integration ID
 * @returns Array of categories
 */
export function getAllIntegrationCategories(
  integration: Integration
): IntegrationCategory[] {
  const categories: IntegrationCategory[] = [];

  for (const [category, integrations] of Object.entries(
    INTEGRATION_CATEGORIES
  )) {
    if (integrations.includes(integration)) {
      categories.push(category as IntegrationCategory);
    }
  }

  return categories;
}

/**
 * Get total count of integrations across all categories
 *
 * @returns Total number of unique integrations
 */
export function getTotalIntegrationsCount(): number {
  const uniqueIntegrations = new Set<Integration>();

  Object.values(INTEGRATION_CATEGORIES).forEach(integrations => {
    integrations.forEach(integration => {
      if (!AllIntegrations[integration]?.hidden) {
        uniqueIntegrations.add(integration);
      }
    });
  });

  return uniqueIntegrations.size;
}

/**
 * Get count of integrations per category
 *
 * @returns Record of category to integration count
 */
export function getCategoryCounts(): Record<IntegrationCategory, number> {
  const counts = {} as Record<IntegrationCategory, number>;

  Object.entries(INTEGRATION_CATEGORIES).forEach(([category, integrations]) => {
    counts[category as IntegrationCategory] = integrations.filter(
      id => !AllIntegrations[id]?.hidden
    ).length;
  });

  return counts;
}
